import axiosInstance from '@/lib/axios';
import { TimelineEntry } from '@/types/models';


// Types
export interface JobPosting {
  _id?: string;
  title: string;
  department: string | { _id?: string; name?: string }; // department ID or department object
  location: string;
  salaryRangeFrom: number;
  salaryRangeTo: number;
  description: string;
  jobType: 'FULL_TIME' | 'PART_TIME' | 'CONTRACT' | 'INTERNSHIP';
  status: string; // Changed from 'OPEN' | 'CLOSED' to string to support dynamic status IDs
  openPositions?: number;
  applicants?: number;
  postedDate?: string;
  createdAt?: string;
  updatedAt?: string;
  isActive?: boolean;
  timeline?: TimelineEntry[];
}

export interface JobFormValues {
  title: string;
  department: string;
  location: string;
  salaryRangeFrom: number;
  salaryRangeTo: number;
  description: string;
  jobType: 'FULL_TIME' | 'PART_TIME' | 'CONTRACT' | 'INTERNSHIP';
  status: string; // Changed from 'OPEN' | 'CLOSED' to string to support dynamic status IDs
}

export interface JobFilters {
  search?: string;
  department?: string;
  status?: string;
  jobType?: string;
  location?: string;
  dateRange?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export interface JobStats {
  totalJobs: number;
  openJobs: number;
  closedJobs: number;
  totalApplicants: number;
  departmentDistribution: { [key: string]: number };
}

// Constants
export const JOB_TYPES = [
  { value: 'FULL_TIME', label: 'Full Time' },
  { value: 'PART_TIME', label: 'Part Time' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'INTERNSHIP', label: 'Internship' }
];

export const JOB_STATUSES = [
  { value: 'OPEN', label: 'Open' },
  { value: 'CLOSED', label: 'Closed' }
];

// Get all job postings
export async function getJobPostings(filters?: JobFilters) {
  let url = '/job-postings';
  const params = new URLSearchParams();

  if (filters) {
    if (filters.search) params.append('search', filters.search);
    if (filters.department) params.append('department', filters.department);
    if (filters.status) params.append('status', filters.status);
    if (filters.jobType) params.append('jobType', filters.jobType);
    if (filters.location) params.append('location', filters.location);
    if (filters.dateRange) params.append('dateRange', filters.dateRange);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const response = await axiosInstance.get(url);
  return response.data;
}

// Get job posting by ID
export async function getJobPostingById(id: string) {
  const response = await axiosInstance.get(`/job-postings/${id}`);
  return response.data;
}

// Create a new job posting
export async function createJobPosting(data: JobFormValues) {
  const response = await axiosInstance.post('/job-postings', data);
  return response.data;
}

// Update job posting
export async function updateJobPosting(id: string, data: Partial<JobFormValues>) {


  try {
    const response = await axiosInstance.patch(`/job-postings/${id}`, data);
    return response.data;
  } catch (error: any) {
    console.error('updateJobPosting - Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      config: error.config
    });
    throw error;
  }
}

// Delete job posting – uses DELETE endpoint directly
export async function deleteJobPosting(id: string): Promise<boolean> {
  try {
    const res = await axiosInstance.delete(`/job-postings/${id}`);
    return res.status === 200 || res.status === 204;
  } catch (error) {
    console.error('deleteJobPosting failed', error);
    return false;
  }
}

// Update job status with automatic timeline management
export async function updateJobStatus(id: string, status: string) {
  try {
    // Use the regular update endpoint
    const response = await axiosInstance.patch(`/job-postings/${id}`, { status });
    const updatedJob = response.data;

    // Temporarily disable timeline creation until backend implements timeline endpoints
    // TODO: Re-enable when backend has timeline endpoints working
    /*
    // Always add timeline entry for status change (even if status is the same, for audit purposes)
    try {
      const timelineEntry = {
        type: 'status_change' as const,
        subType: 'status_change',
        title: `Status changed to ${status}`,
        description: previousStatus && previousStatus !== status 
          ? `Job status updated from ${previousStatus} to ${status}${notes ? `. Notes: ${notes}` : ''}`
          : `Job status set to ${status}${notes ? `. Notes: ${notes}` : ''}`,
        metadata: {
          previousStatus: previousStatus || 'unknown',
          newStatus: status,
          notes: notes || null,
          updatedAt: new Date().toISOString()
        }
      };
      
      await timelineService.addJobTimelineEntry(id, timelineEntry);
      
      // Update the job's timeline in the response
      if (updatedJob && !updatedJob.timeline) {
        updatedJob.timeline = [];
      }
      if (updatedJob && updatedJob.timeline) {
        updatedJob.timeline.push(timelineEntry as any);
      }
      
    } catch (timelineError) {
      console.warn('Failed to add timeline entry for status change:', timelineError);
    }
    */

    return updatedJob;
  } catch (error) {
    console.error('Error updating job status:', error);
    throw error;
  }
}

// Get job posting statistics
export async function getJobStats(): Promise<JobStats> {
  const response = await axiosInstance.get('/job-postings/stats');
  return response.data;
}

// Get job postings by department
export async function getJobPostingsByDepartment(departmentId: string) {
  const response = await axiosInstance.get(`/job-postings/department/${departmentId}`);
  return response.data;
}

// Search job postings
export async function searchJobPostings(query: string) {
  const response = await axiosInstance.get(`/job-postings/search?q=${encodeURIComponent(query)}`);
  return response.data;
}

// Get open job postings (public endpoint)
export async function getOpenJobPostings() {
  const response = await axiosInstance.get('/job-postings/open');
  return response.data;
}  